/**
 * Development-only console logging.
 *
 * A drop-in replacement for `console` that only logs in development mode.
 * Use this instead of `console` for debug messages that shouldn't appear in production.
 *
 * @example
 * ```typescript
 * import { consoleDev } from "@shared/lib/debug";
 *
 * consoleDev.log("Debug message");
 * consoleDev.warn("Warning");
 * consoleDev.error("Error");
 * ```
 */

function isDev(): boolean {
  return import.meta.env.DEV && !import.meta.env.VITEST;
}

/* eslint-disable no-console */
export const consoleDev = {
  log(...args: unknown[]): void {
    if (isDev()) console.log(...args);
  },
  warn(...args: unknown[]): void {
    if (isDev()) console.warn(...args);
  },
  error(...args: unknown[]): void {
    if (isDev()) console.error(...args);
  },
  info(...args: unknown[]): void {
    if (isDev()) console.info(...args);
  },
  debug(...args: unknown[]): void {
    if (isDev()) console.debug(...args);
  },
  time(label: string): void {
    if (isDev()) console.time(label);
  },
  timeEnd(label: string): void {
    if (isDev()) console.timeEnd(label);
  },
  group(...args: unknown[]): void {
    if (isDev()) console.group(...args);
  },
  groupEnd(): void {
    if (isDev()) console.groupEnd();
  },
  table(data: unknown): void {
    if (isDev()) console.table(data);
  },
};
/* eslint-enable no-console */

/**
 * Development-only performance API.
 *
 * A drop-in replacement for `performance` that only records marks/measures in development mode.
 * Use this for debug instrumentation that shouldn't clutter production DevTools timelines.
 *
 * @example
 * ```typescript
 * import { performanceDev } from "@shared/lib/console-dev";
 *
 * performanceDev.mark('tool-start');
 * // ... work ...
 * performanceDev.measure('my-tool', 'tool-start');
 * ```
 */
export const performanceDev: Performance = new Proxy(performance, {
  get(target, prop, receiver) {
    const value = Reflect.get(target, prop, receiver);
    if (typeof value === "function") {
      return (...args: unknown[]) => (isDev() ? value.apply(target, args) : undefined);
    }
    return value;
  },
});
